import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { FC, ReactNode } from 'react';
import { userService } from '@/services/userService';
// import { authApi, tokenManager } from '@/services/authApi';
import { authApi } from '@/services/authApi';
import type { User, AuthResponse, UserProfile, UserRole } from '@/types';
// import api from '@/services/api';

interface UserContextType {
  user?: User | null;
  login: (email: string, password: string) => Promise<AuthResponse>;
  logedin: (userData: Omit<User, 'isAuthenticated'>) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
  updateUser: (updates: Partial<User>) => void;
  refreshUser: () => Promise<void>;
  isRefreshing: boolean;
  isAuthenticated: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start as true for initial auth check
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Use ref to track initial mount and prevent unnecessary re-renders
  const isInitialMount = useRef(true);

  // Convert UserProfile to our User interface
  const mapUserProfileToUser = useCallback((userProfile: UserProfile | undefined | null, authResponse?: AuthResponse): User => {
    const profile = userProfile ?? authResponse?.user;
    const phoneCode = profile?.phone_code ?? profile?.mobile_region_code;
    const phone = profile?.phone ?? profile?.mobile_number;
    
    return {
      id: profile?.id ?? 0,
      email: profile?.email ?? '',
      name: `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim(),
      first_name: profile?.first_name ?? '',
      last_name: profile?.last_name ?? '',
      phone_code: phoneCode,
      phone: phone,
      country: profile?.country,
      gender: profile?.gender,
      dob: profile?.dob,
      employment_status: profile?.employment_status,
      mobile_number: profile?.mobile_number,
      mobile_region_code: profile?.mobile_region_code,
      highest_education: profile?.highest_education,
      fk_role_id: profile?.fk_role_id,
      role: (profile?.fk_role_id === 3 ? 'plus' : 'visitor') as UserRole,
      created_at: profile?.created_at,
      updated_at: profile?.updated_at,
      avatar: profile?.avatar,
      isAuthenticated: true,
      profile: profile
    };
  }, []);

  // Logout function - make it stable and remove from dependencies
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Call backend logout endpoint if we have a token
      const token = localStorage.getItem('auth_token');
      if (token) {
        await userService.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear tokens and user state
      userService.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);

  // Refresh user data from backend
  const refreshUser = useCallback(async (): Promise<void> => {
    if (isRefreshing) {
      // Wait for the existing refresh to complete
      return new Promise<void>((resolve) => {
        const checkInterval = setInterval(() => {
          if (!isRefreshing) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve();
        }, 5000);
      });
    }

    // Check if we just logged in - don't clear tokens if so
    const lastLoginTime = localStorage.getItem('last_login_time');
    const justLoggedIn = lastLoginTime && (Date.now() - parseInt(lastLoginTime)) < 10000; // 10 seconds

    setIsRefreshing(true);
    try {
      const response = await userService.getCurrentUser();
      const userData = mapUserProfileToUser(response.user as UserProfile, response as AuthResponse);
      
      setUser(userData);
      setIsAuthenticated(true);
      
    } catch (error: any) {
      console.error('Failed to refresh user:', error);
      const refreshToken = localStorage.getItem('refresh_token');
      
      // If we just logged in, don't clear tokens on error - might be a timing issue
      if (justLoggedIn) {
        console.warn('Failed to refresh user right after login, but keeping tokens (might be timing issue)');
        // Don't clear tokens or logout - just set state
        setUser(null);
        setIsAuthenticated(false);
        return;
      }
      
      // If we can't refresh, check if we should try token refresh
      // Only try refresh if we haven't just refreshed (check last refresh attempt)
      if (error?.response?.status === 401 && refreshToken) {
        const lastRefreshAttempt = localStorage.getItem('last_refresh_attempt');
        const REFRESH_COOLDOWN = 3000; // 3 seconds
        
        // If we just refreshed recently, don't try again - the token might be invalid
        if (lastRefreshAttempt && Date.now() - parseInt(lastRefreshAttempt) < REFRESH_COOLDOWN) {
          console.warn('Recent refresh attempt detected, token may be invalid');
          await logout();
          return;
        }
        
        try {
          // Add retry logic with max attempts
          await refreshTokenWithRetry();
          // Small delay to ensure token is updated in localStorage
          await new Promise(resolve => setTimeout(resolve, 100));
          // Retry getting user data after token refresh
          const response = await userService.getCurrentUser();
          const userData = mapUserProfileToUser(response.user as UserProfile, response as AuthResponse);
          setUser(userData);
          setIsAuthenticated(true);
          return;
        } catch (refreshError) {
          console.error('Token refresh also failed:', refreshError);
          // Only logout if token refresh fails
          await logout();
        }
      } else {
        // For other errors, don't logout - just set as not authenticated
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, mapUserProfileToUser, logout]);

  // Add this helper function
  const refreshTokenWithRetry = async (maxRetries = 1, retryCount = 0): Promise<void> => {
    try {
      await authApi.refreshToken();
    } catch (error: any) {
      // If refresh token itself is invalid (401), don't retry
      if (error?.response?.status === 401) {
        throw new Error('Refresh token invalid - requiring reauthentication');
      }
      
      // Retry for network errors or server errors
      if (retryCount < maxRetries && (error?.code === 'NETWORK_ERROR' || error?.response?.status >= 500)) {
        console.log(`Retrying token refresh (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return refreshTokenWithRetry(maxRetries, retryCount + 1);
      }
      
      throw error;
    }
  };

  // Initialize auth - separate function that doesn't depend on logout
  const initializeAuth = useCallback(async (): Promise<void> => {
    try {
      // Check for token in query parameters first
      const urlParams = new URLSearchParams(window.location.search);
      const queryToken = urlParams.get('token');
      
      if (queryToken) {
        console.log('initializeAuth: Found token in query parameters');
        // Store the token from query parameter
        localStorage.setItem('auth_token', queryToken);
        
        // Try to decode token to get expiry (if it's a JWT)
        const tokenExpiry = userService.getTokenExpiry(queryToken);
        if (tokenExpiry) {
          localStorage.setItem('token_expiry', tokenExpiry.toString());
        } else {
          // Default to 30 minutes if we can't decode expiry
          const defaultExpiry = Date.now() + (30 * 60 * 1000);
          localStorage.setItem('token_expiry', defaultExpiry.toString());
        }
        
        // Remove token from URL to keep it clean
        urlParams.delete('token');
        const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
        window.history.replaceState({}, '', newUrl);
        
        // Try to fetch user data to verify the token
        try {
          await refreshUser();
          console.log('initializeAuth: Successfully authenticated with query token');
          return;
        } catch (error) {
          console.error('initializeAuth: Failed to verify query token:', error);
          // If token verification fails, clear it and continue with normal flow
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token_expiry');
        }
      }
      
      const token = localStorage.getItem('auth_token');
      const refreshToken = localStorage.getItem('refresh_token');

      // Check if we just logged in (within last 5 seconds) - don't clear tokens if so
      const lastLoginTime = localStorage.getItem('last_login_time');
      const justLoggedIn = lastLoginTime && (Date.now() - parseInt(lastLoginTime)) < 5000;
      
      console.log('initializeAuth: Checking tokens', {
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        tokenExpiry: localStorage.getItem('token_expiry'),
        justLoggedIn
      });
      
      // If we just logged in, skip initialization to avoid clearing tokens
      if (justLoggedIn) {
        console.log('initializeAuth: Just logged in, skipping initialization to avoid clearing tokens');
        // Still try to fetch user data if we have tokens
        if (token && refreshToken) {
          try {
            await refreshUser();
          } catch (error) {
            console.warn('Failed to fetch user after login:', error);
            // Don't clear tokens on error right after login
          }
        }
        return;
      }

      // If we have both tokens but token is expired and refresh token is old, logout immediately
      if (token && refreshToken) {
        const tokenExpiry = localStorage.getItem('token_expiry');
        const refreshTokenTimestamp = localStorage.getItem('refresh_token_timestamp');
        const refreshTokenAge = refreshTokenTimestamp ? Date.now() - parseInt(refreshTokenTimestamp) : 0;
        
        // Validate tokenExpiry is a valid number before checking
        if (tokenExpiry) {
          const expiryTime = parseInt(tokenExpiry);
          if (!isNaN(expiryTime) && expiryTime > 0) {
            const isExpired = Date.now() >= expiryTime;
            if (isExpired && refreshTokenAge > 24 * 60 * 60 * 1000) {
              // Token expired and refresh token is more than 24 hours old
              console.warn('Stale tokens detected, forcing logout');
              await logout();
              return;
            }
          } else {
            console.warn('Invalid token_expiry during initializeAuth:', tokenExpiry, '- skipping stale check');
          }
        }
      }
      
      if (!token || !refreshToken) {
        // No tokens, user is not authenticated
        console.log('initializeAuth: No tokens found, setting unauthenticated');
        setIsAuthenticated(false);
        setUser(null);
        return;
      }

      // Check if token is expired
      const tokenExpiry = localStorage.getItem('token_expiry');
      // Validate tokenExpiry is a valid number (not NaN)
      let isTokenExpired = true;
      if (tokenExpiry) {
        const expiryTime = parseInt(tokenExpiry);
        if (!isNaN(expiryTime) && expiryTime > 0) {
          isTokenExpired = Date.now() >= expiryTime;
        } else {
          console.warn('Invalid token_expiry value:', tokenExpiry, '- treating as expired');
          // If tokenExpiry is invalid, don't clear tokens immediately - might be a storage issue
          // Instead, try to refresh if we have a refresh token
          isTokenExpired = true;
        }
      }

      // Validate refresh token exists and isn't too old
      const refreshTokenTimestamp = localStorage.getItem('refresh_token_timestamp');
      const REFRESH_TOKEN_MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 days in ms
      
      if (refreshTokenTimestamp) {
        const refreshTokenAge = Date.now() - parseInt(refreshTokenTimestamp);
        if (refreshTokenAge > REFRESH_TOKEN_MAX_AGE) {
          console.warn('Refresh token too old, requiring re-login');
          await logout();
          return;
        }
      }

      // Don't fetch user data if we just logged in - we already have user data from login
      if (justLoggedIn) {
        console.log('initializeAuth: Just logged in, skipping user fetch (already have user data)');
        return;
      }

      if (isTokenExpired && refreshToken) {
        // Token expired but we have refresh token - try to refresh
        console.log('initializeAuth: Token expired, attempting refresh');
        try {
          await authApi.refreshToken();
          // Small delay to ensure token is updated in localStorage before making API calls
          await new Promise(resolve => setTimeout(resolve, 100));
          await refreshUser();
        } catch (refreshError) {
          console.warn('Token refresh failed during initialization:', refreshError);
          const status = (refreshError as any)?.response?.status || (refreshError as any)?.status;
          
          // If refresh token is invalid (401), logout immediately
          if (status === 401) {
            console.error('Refresh token invalid (401) during initialization, logging out');
            await logout();
          } else {
            // For other errors (network, etc.), don't logout - just set state
            // User might have network issues, tokens are still valid
            console.warn('Token refresh failed with non-401 error, keeping tokens but setting unauthenticated');
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } else if (!isTokenExpired) {
        // Token is valid, fetch user data
        console.log('initializeAuth: Token valid, fetching user data');
        await refreshUser();
      } else {
        // No valid tokens
        console.log('initializeAuth: No valid tokens, setting unauthenticated');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, [refreshUser, logout]); // Add logout to dependencies

  // Check for existing auth token on app load
  useEffect(() => {
    const init = async () => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        await initializeAuth();
        setIsLoading(false);
      }
    };

    init();
  }, [initializeAuth]); // Only depend on initializeAuth

  // Remove the duplicate fetchUser function as it's not needed

  // Login function to set user from external source
  const logedin = useCallback((userData: Omit<User, 'isAuthenticated'>) => {
    const userWithAuth: User = {
      ...userData,
      isAuthenticated: true,
    };
    
    setUser(userWithAuth);
    setIsAuthenticated(true);
  }, []);

  // Update user data locally
  const updateUser = useCallback((updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
    }
  }, [user]);

  // Login function with credentials
  const login = useCallback(async (email: string, password: string): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      // Login and fetch user data
      const response = await userService.loginAndFetchUser({ email, password });
      
      const userData = mapUserProfileToUser(response.user, response);
      setUser(userData);
      setIsAuthenticated(true);

      // Store tokens (already done in loginAndFetchUser, but ensure they're set)
      userService.updateTokens(response);
      
      // Mark that we just logged in to prevent initializeAuth from clearing tokens
      localStorage.setItem('last_login_time', Date.now().toString());
      console.log('Login successful, tokens stored');

      return response;
    } catch (error) {
      console.error('Login failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [mapUserProfileToUser]);

  const value: UserContextType = {
    user,
    login,
    logout,
    logedin,
    isLoading,
    updateUser,
    refreshUser,
    isRefreshing,
    isAuthenticated,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Simplified useAuth hook
export const useAuth = () => {
  const context = useUser();
  
  return {
    user: context.user,
    login: context.login,
    logout: context.logout,
    isLoading: context.isLoading,
    isAuthenticated: context.isAuthenticated,
    hasRole: (role: string) => context.user?.role === role,
  };
};